import React, { useState, useEffect } from 'react';
import Login from './Login';
import LandingPage, { PaginaReferidos, PaginaQuienessomos } from './LandingPage';
import AppCliente from './modules/AppCliente';
import AppChofer from './modules/AppChofer';
import AppTransportista from './modules/AppTransportista';
import AppSuperAdmin from './modules/AppSuperAdmin';
import { getUsuarioGuardado, getToken, clearTokens, apiMe, apiVerifyEmail } from './api';
import { useSocket } from './hooks/useSocket';
import { usePushNotifications } from './hooks/usePushNotifications';
import { useAppInstaladaSync } from './hooks/useAppInstaladaSync';
import { phIdentify, phReset, phCapture } from './posthog';
import { sentryIdentify, sentryReset } from './sentry';
import InstallBanner from './components/InstallBanner';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[Choyero] Error no capturado:', error, info);
  }
  handleReset() {
    clearTokens();
    window.location.reload();
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen bg-gray-900 p-6">
          <div className="bg-white rounded-2xl shadow-2xl p-10 max-w-md w-full text-center">
            <p className="text-5xl mb-4">⚠️</p>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Algo salió mal</h2>
            <p className="text-gray-500 text-sm mb-6">
              Ocurrió un error inesperado. Puedes intentar reiniciar la aplicación.
            </p>
            <p className="text-xs text-red-400 font-mono bg-red-50 rounded p-2 mb-6 text-left break-all">
              {this.state.error?.message || 'Error desconocido'}
            </p>
            <button
              onClick={() => this.handleReset()}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold"
            >
              Reiniciar aplicación
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [usuario, setUsuario] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [mostrarLogin, setMostrarLogin] = useState(null);
  const [verifyState, setVerifyState] = useState(null); // null | 'pending' | 'success' | 'error'
  const [verifyMsg, setVerifyMsg] = useState('');
  const [resetTokenUrl, setResetTokenUrl] = useState(null); // token de recuperación desde URL

  // WebSocket — se conecta cuando hay usuario autenticado
  useSocket(usuario);

  // Push Notifications — suscribir dispositivo cuando hay sesión activa
  usePushNotifications(usuario, getToken());

  // Avisa al backend si la app corre instalada (para el conteo del admin)
  useAppInstaladaSync(usuario);

  // Restaurar sesión al cargar desde JWT
  useEffect(() => {
    const restaurar = async () => {
      const token = getToken();
      if (!token) { setCargando(false); return; }

      // Usar usuario guardado en localStorage mientras verificamos con el servidor
      const usuarioGuardado = getUsuarioGuardado();
      if (usuarioGuardado) setUsuario(usuarioGuardado);

      try {
        // Timeout de 5 segundos — si el backend tarda más (ej. Railway durmiendo)
        // mostramos la página en lugar de quedarnos congelados en el splash
        const usuarioActual = await Promise.race([
          apiMe(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000))
        ]);
        setUsuario(usuarioActual);
      } catch (e) {
        if (e?.message === 'timeout') {
          // Backend tardó demasiado — no borrar token, el usuario podrá reintentar al refrescar
          setUsuario(null);
        } else {
          // Token inválido o expirado — sí limpiar sesión
          clearTokens();
          setUsuario(null);
        }
      } finally {
        setCargando(false);
      }
    };
    restaurar();
  }, []);

  // Handle ?verify=TOKEN from email verification links
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verifyToken = params.get('verify');
    const resetToken  = params.get('reset');

    // Limpiar params de la URL para evitar re-ejecución en refresh
    if (verifyToken || resetToken) {
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    }

    if (verifyToken) {
      setVerifyState('pending');
      apiVerifyEmail(verifyToken)
        .then((res) => { setVerifyState('success'); setVerifyMsg(res?.mensaje || '¡Email verificado correctamente!'); })
        .catch((e) => { setVerifyState('error'); setVerifyMsg(e.message || 'El enlace es inválido o ya expiró.'); });
    }

    if (resetToken) {
      setResetTokenUrl(resetToken);
      setMostrarLogin('login');
    }

    // Link de referido con tipo específico: ?ref=CHO-XXX&tipo=cliente|transportista
    // Sin &tipo → mostrar landing page (el ?ref queda en URL para cuando se registre)
    const refParam = params.get('ref');
    const tipoParam = params.get('tipo');
    if (refParam && tipoParam && !verifyToken && !resetToken) {
      if (tipoParam === 'cliente')             setMostrarLogin('cliente');
      else if (tipoParam === 'transportista')  setMostrarLogin('transportista');
      // tipo=referidor → /referidos lo maneja; otros tipos → landing page
    }
  }, []); // eslint-disable-line

  const handleLogin = (user) => {
    setUsuario(user);
    phIdentify(user, user.empresa);
    sentryIdentify(user);
    phCapture('usuario_login', { rol: user.rol });
  };

  const handleLogout = async () => {
    phReset();
    sentryReset();
    clearTokens();
    setUsuario(null);
    setMostrarLogin('login');
  };

  // Email verification screen
  if (verifyState === 'pending' || verifyState === 'success' || verifyState === 'error') {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-orange-900">
        <div className="bg-white/5 border border-white/10 rounded-2xl max-w-md w-full p-10 text-center mx-4">
          {verifyState === 'pending' && (
            <>
              <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
              <p className="text-white text-lg font-semibold">Verificando tu email…</p>
            </>
          )}
          {verifyState === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-green-400"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">¡Email verificado!</h2>
              <p className="text-gray-400 mb-8">{verifyMsg}</p>
              <button onClick={() => { setVerifyState(null); setMostrarLogin('login'); }}
                className="w-full py-3 bg-orange-500 hover:bg-orange-400 text-white rounded-xl font-bold transition">
                Iniciar sesión
              </button>
            </>
          )}
          {verifyState === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-red-400"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Enlace inválido</h2>
              <p className="text-gray-400 mb-8">{verifyMsg}</p>
              <button onClick={() => { setVerifyState(null); setMostrarLogin('login'); }}
                className="w-full py-3 bg-orange-500 hover:bg-orange-400 text-white rounded-xl font-bold transition">
                Ir a iniciar sesión
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (cargando) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: '#0f1b2d' }}>
        <div className="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Ruta /quienessomos para usuarios logueados (sin modal de login)
  if (window.location.pathname === '/quienessomos' && usuario) {
    return <PaginaQuienessomos onIniciarSesion={null} onRegistrar={null} />;
  }

  if (!usuario) {
    const urlLogin = new URLSearchParams(window.location.search).get('login');
    const vistaActual = mostrarLogin || (urlLogin ? 'login' : null);

    // Ruta /referidos — página exclusiva para registro MLM
    if (window.location.pathname === '/referidos' && !vistaActual) {
      return (
        <PaginaReferidos
          onRegistrar={() => setMostrarLogin('referidor')}
          onIniciarSesion={() => setMostrarLogin('login')}
        />
      );
    }

    // Ruta /quienessomos para visitantes — cede paso al modal cuando se abre
    if (window.location.pathname === '/quienessomos' && !vistaActual) {
      return (
        <PaginaQuienessomos
          onIniciarSesion={() => setMostrarLogin('login')}
          onRegistrar={() => setMostrarLogin('elegir')}
        />
      );
    }

    if (vistaActual) {
      return (
        <Login
          onLogin={handleLogin}
          vistaInicial={vistaActual === 'login' ? null : vistaActual}
          onVolver={() => setMostrarLogin(null)}
          resetTokenInicial={resetTokenUrl}
        />
      );
    }
    return (
      <>
        <InstallBanner />
        <LandingPage
          onIniciarSesion={() => setMostrarLogin('login')}
          onRegistrar={() => setMostrarLogin('elegir')}
          onRegistrarCliente={() => setMostrarLogin('cliente')}
          onRegistrarTransportista={() => setMostrarLogin('transportista')}
        />
      </>
    );
  }

  // Routing por rol
  const moduloProps = { usuario, onLogout: handleLogout };

  let modulo;
  switch (usuario.rol) {
    case 'cliente':       modulo = <AppCliente {...moduloProps} />; break;
    case 'chofer':        modulo = <AppChofer {...moduloProps} />; break;
    case 'transportista': modulo = <AppTransportista {...moduloProps} />; break;
    case 'superadmin':
    case 'admin':
    case 'operador':
    case 'soporte':       modulo = <AppSuperAdmin {...moduloProps} />; break;
    default:
      clearTokens();
      return <Login onLogin={handleLogin} />;
  }

  return (
    <>
      <InstallBanner />
      <ErrorBoundary key={usuario.id}>{modulo}</ErrorBoundary>
    </>
  );
}

export default App;
